/**
 * What the selected workspace is currently allowed to do.
 *
 * One fetch of GET /api/v1/tenant/access answers both questions the shell has to settle before it
 * renders anything: has this workspace finished being set up, and is its subscription still paying
 * for what it is about to be asked to do. Neither is inferred here. Both are decided by the same
 * code that will answer the next write, so the screen somebody is shown and the reply their next
 * action receives cannot disagree.
 *
 * The shape mirrors WorkspaceAccess in edge/api/src/modules/tenancy/onboarding.ts.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect } from "react";
import { api, setWorkspaceReadOnly } from "../api/client";
import { useAuth } from "../auth/AuthProvider";

/** The stages platform.tenants.onboarding_state admits, in the order the wizard walks them. */
export const onboardingStages = [
  "owner_security",
  "billing",
  "configuration",
  "venue_setup",
  "ready",
] as const;

export type OnboardingStage = (typeof onboardingStages)[number];

export interface OnboardingStep {
  key: OnboardingStage;
  required: boolean;
  satisfied: boolean;
  note?: string;
}

export interface OnboardingStatus {
  state: string;
  currentStep: OnboardingStage;
  complete: boolean;
  steps: OnboardingStep[];
  payoutsEnabled: boolean;
  subscriptionStatus: string;
  connectedAccountState: string;
}

export interface WorkspaceAccess {
  onboarding: OnboardingStatus;
  entitlementState: string;
  readOnly: boolean;
  limited: boolean;
  lapsedSince: string | null;
  readOnlyDaysRemaining: number | null;
  wall: boolean;
}

export const workspaceAccessKey = ["tenant", "access"] as const;

const WorkspaceAccessContext = createContext<WorkspaceAccess | null>(null);

/**
 * The current standing, or null when there is nothing to stand on: no workspace is selected, or the
 * read failed. Callers must treat null as "no restriction known" rather than as "restricted", since
 * refusing on an unanswered question would lock people out of a working workspace over a dropped
 * request. The server refuses what must be refused either way.
 */
export function useWorkspaceAccess(): WorkspaceAccess | null {
  return useContext(WorkspaceAccessContext);
}

/**
 * The gate's own fetch.
 *
 * Off until a workspace is selected, because it is scoped to one. `enabled` is a parameter rather
 * than a condition around the call because a hook cannot be conditional: the platform console is
 * not tenant work and must not wait on, or provoke, a tenant read.
 */
export function useWorkspaceAccessQuery(enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: workspaceAccessKey,
    queryFn: () => api<WorkspaceAccess>("/api/v1/tenant/access"),
    enabled: enabled && Boolean(auth.session?.selectedMembership),
    // Short rather than infinite: paying, enrolling an authenticator and finishing Stripe all change
    // this answer, and each of them happens on another page or another site.
    staleTime: 10_000,
  });
}

/**
 * Re-reads the standing after something that changes it, such as a completed payment.
 *
 * Stable across renders, because callers put it in effect dependencies and a fresh function every
 * render would re-run those effects every render.
 */
export function useRefreshWorkspaceAccess(): () => Promise<void> {
  const client = useQueryClient();
  return useCallback(async () => {
    await client.invalidateQueries({ queryKey: workspaceAccessKey });
  }, [client]);
}

/**
 * Publishes the standing to the tree, and mirrors the read-only half into the API client.
 *
 * The mirror is why this is a component rather than a plain context: api() is a plain function with
 * no access to React, and it is the one place every mutating request passes through. Clearing the
 * flag on unmount matters as much as setting it, because signing out or switching workspace must
 * not leave the next workspace wearing the last one's restriction.
 */
export function WorkspaceAccessProvider({
  value,
  children,
}: {
  value: WorkspaceAccess | null;
  children: ReactNode;
}) {
  const readOnly = value?.readOnly === true;
  useEffect(() => {
    setWorkspaceReadOnly(readOnly);
    return () => setWorkspaceReadOnly(false);
  }, [readOnly]);
  return (
    <WorkspaceAccessContext.Provider value={value}>{children}</WorkspaceAccessContext.Provider>
  );
}
